import { zodResolver } from "@hookform/resolvers/zod";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@marketplace/ui/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useMemo, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ProductQueryKeys } from "@/modules/product/generators/product-query-keys";
import { useGetProductListInfiniteQuery } from "@/modules/product/hooks/queries/use-get-product-list-infinite-query";

import { exportOrderModalAtom } from "../../atoms/export-order-modal-atom";
import { OrderService } from "../../services/order-service";
import {
  type ExportOrderFormSchema,
  useExportOrderFormSchema
} from "../schemas/use-export-order-form-schema";

type FilterData = {
  q?: string;
  start_date: string;
  end_date: string;
};

type ExportState = "form" | "processing" | "done";

export const useExportOrderForm = (filters: FilterData) => {
  const [modalState] = useAtom(exportOrderModalAtom);
  const resetModal = useResetAtom(exportOrderModalAtom);
  const queryClient = useQueryClient();
  const schema = useExportOrderFormSchema();
  const [exportState, setExportState] = useState<ExportState>("form");
  const { t } = useTranslation("order", {
    keyPrefix: "orderListPage.exportModal"
  });

  const formMethods = useForm<ExportOrderFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      productTypes: [],
      productIds: [],
      orderStatuses: [],
      exportFormat: "csv",
      doneSendTo: "device" as const,
      start_date: filters.start_date ?? "",
      end_date: filters.end_date ?? ""
    },
    mode: "onChange"
  });

  const { handleSubmit, trigger, reset } = formMethods;

  const watchedCategories = formMethods.watch("productTypes");
  const [productSearch, setProductSearch] = useState("");
  const [debouncedProductSearch] = useDebouncedValue(productSearch, 300);

  const {
    data: productData,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useGetProductListInfiniteQuery({
    queryParams: {
      q: debouncedProductSearch || undefined,
      limit: 10,
      product_status: "active",
      sort: "a-z"
    },
    options: {
      enabled: modalState.visible
    }
  });

  const productOptions = useMemo(() => {
    if (!productData?.pages) return [];

    const results = productData.pages.flatMap((page) =>
      page.data.map((product) => ({
        value: product.id,
        label: product.name,
        category: product.product_category?.name?.toLowerCase() ?? ""
      }))
    );

    if (watchedCategories.length === 1) {
      const cat = watchedCategories[0];
      return results.filter((p) => p.category === cat);
    }

    return results;
  }, [productData, watchedCategories]);

  const handleDownload: SubmitHandler<ExportOrderFormSchema> = async (
    values
  ) => {
    const payload = {
      start_date: values.start_date || undefined,
      end_date: values.end_date || undefined,
      product_ids: values.productIds ?? [],
      product_types: values.productTypes,
      order_statuses: values.orderStatuses,
      file_type: values.exportFormat,
      q: filters.q || undefined,
      send_to: values.doneSendTo
    };

    if (values.doneSendTo === "email") {
      try {
        await OrderService.exportOrderList(payload);
        handleClose();
        notifications.show({
          message: t("exportByEmail")
        });
      } catch {
        notifications.show({
          message: t("errors.exportFailed"),
          variant: "error"
        });
      }
      return;
    }

    setExportState("processing");

    try {
      const blob = await OrderService.exportOrderList(payload);

      await new Promise((resolve) => setTimeout(resolve, 1000));

      const ext = values.exportFormat === "csv" ? "csv" : "xlsx";
      const filename = `orders-export-${new Date().toISOString().replace(/[:.]/g, "-")}`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${filename}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setExportState("done");
    } catch {
      notifications.show({
        message: t("errors.exportFailed"),
        variant: "error"
      });
      setExportState("form");
    }
  };

  const handleClose = () => {
    setExportState("form");
    setProductSearch("");
    queryClient.removeQueries({
      queryKey: ProductQueryKeys.getProductListInfinite._def
    });
    reset({
      productTypes: [],
      productIds: [],
      orderStatuses: [],
      exportFormat: "csv",
      doneSendTo: "device" as const,
      start_date: filters.start_date ?? "",
      end_date: filters.end_date ?? ""
    });
    resetModal();
  };

  return {
    formMethods,
    exportState,
    productOptions,
    isModalVisible: modalState.visible,
    isProductFetching: isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    productSearch,
    setProductSearch,
    handleSubmitForm: handleSubmit(handleDownload),
    handleDownloadClick: async () => {
      const isValid = await trigger();
      if (!isValid) return;
      return handleSubmit(handleDownload)();
    },
    handleClose
  };
};
